<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

/* global axios */

export default {
  components: { NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiToken: '',
      channelId: '',
      channels: [],
      loadingChannels: false,
      channelError: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    selectedChannel() {
      if (!this.channelId) return null;
      return this.channels.find(ch => ch.id === this.channelId);
    },
  },
  validations: {
    inboxName: { required },
    apiToken: { required },
    channelId: { required },
  },
  methods: {
    async fetchChannels() {
      if (!this.apiToken) return;
      this.loadingChannels = true;
      this.channelError = '';
      this.channels = [];
      this.channelId = '';
      this.phoneNumber = '';
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.$route.params.accountId}/notificame/channels`,
          { params: { token: this.apiToken } }
        );
        this.channels = data.channels || [];
        // Auto-select if only one channel
        if (this.channels.length === 1) {
          this.selectChannel(this.channels[0]);
        }
        if (this.channels.length === 0) {
          this.channelError = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_NO_CHANNELS'
          );
        }
      } catch {
        this.channelError = this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_FETCH_ERROR'
        );
      } finally {
        this.loadingChannels = false;
      }
    },
    selectChannel(ch) {
      this.channelId = ch.id;
      // Auto-fill phone number from channel name or phone field
      this.phoneNumber = ch.phone || ch.name || ch.id;
      // Auto-fill inbox name if empty
      if (!this.inboxName) {
        this.inboxName = ch.name || `HubNavi ${ch.id}`;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName?.trim(),
          channel: {
            type: 'whatsapp',
            phone_number: this.phoneNumber,
            provider: 'notificame',
            provider_config: {
              api_token: this.apiToken,
              channel_id: this.channelId,
            },
          },
        });
        this.$router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: inbox.id },
        });
      } catch (error) {
        useAlert(error.message);
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
    <!-- Step 1: API Token -->
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiToken.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_TOKEN.LABEL') }}
        <input
          v-model="apiToken"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_TOKEN.PLACEHOLDER')
          "
          @blur="fetchChannels"
        />
        <span v-if="v$.apiToken.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_TOKEN.ERROR') }}
        </span>
      </label>
    </div>

    <!-- Loading channels -->
    <div v-if="loadingChannels" class="flex items-center gap-2 py-3">
      <span class="i-lucide-loader-2 w-4 h-4 animate-spin" />
      <span class="text-sm text-slate-500">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_LOADING') }}
      </span>
    </div>

    <!-- Channel selector (only shown when channels loaded) -->
    <div v-if="channels.length > 1" class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.channelId.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_CHANNEL.LABEL') }}
        <select
          v-model="channelId"
          @change="
            selectChannel(channels.find(ch => ch.id === channelId) || {})
          "
          @blur="v$.channelId.$touch"
        >
          <option value="" disabled>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_CHANNEL.PLACEHOLDER') }}
          </option>
          <option v-for="ch in channels" :key="ch.id" :value="ch.id">
            {{ ch.name || ch.id }}
          </option>
        </select>
        <span v-if="v$.channelId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_CHANNEL.ERROR') }}
        </span>
      </label>
    </div>

    <!-- Selected channel info -->
    <div
      v-if="channelId && !loadingChannels"
      class="flex items-center gap-2 p-3 mb-4 rounded-lg bg-green-50 border border-green-200"
    >
      <span class="i-lucide-check-circle w-4 h-4 text-green-600" />
      <span class="text-sm text-green-700">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOTIFICAME_CONNECTED') }}:
        {{ selectedChannel?.name || channelId }}
      </span>
    </div>

    <!-- Error message -->
    <div
      v-if="channelError"
      class="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200"
    >
      <span class="i-lucide-alert-circle w-4 h-4 text-red-600" />
      <span class="text-sm text-red-700">{{ channelError }}</span>
    </div>

    <!-- Inbox name (shown after channel selected) -->
    <div v-if="channelId" class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div v-if="channelId" class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
